import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';

interface ExternalApiKey {
  id: number;
  name: string;
  key_prefix: string;
  rate_limit: number;
  enabled: number;
  created_at: string;
}

interface PseudoProject {
  id: number;
  name: string;
}

interface Project {
  id: number;
  name: string;
  status: string;
}

interface SharedProject {
  id: number;
  pseudo_project_id: number | null;
  project_id: number | null;
  type: 'pseudo_project' | 'project';
}

interface GenerateKeyResult {
  plaintext: string;
  key: ExternalApiKey;
}

@customElement('cp-external-agent-settings')
export class CpExternalAgentSettings extends LitElement {
  createRenderRoot() { return this; }

  // API Keys section
  @state() private _keys: ExternalApiKey[] = [];
  @state() private _loadingKeys = true;
  @state() private _newKeyName = '';
  @state() private _newKeyRateLimit = 100;
  @state() private _generatingKey = false;
  @state() private _generatedKey: string | null = null;
  @state() private _copySuccess = false;

  // Projects section
  @state() private _pseudoProjects: PseudoProject[] = [];
  @state() private _projects: Project[] = [];
  @state() private _sharedPseudoIds = new Set<number>();
  @state() private _sharedProjectIds = new Set<number>();
  @state() private _loadingProjects = true;
  @state() private _togglingId: string | null = null;

  // Global
  @state() private _error = '';
  @state() private _successMessage = '';

  connectedCallback(): void {
    super.connectedCallback();
    this._loadKeys();
    this._loadProjects();
  }

  private async _loadKeys(): Promise<void> {
    this._loadingKeys = true;
    try {
      const res = await fetch('/api/external-agent/keys', { credentials: 'include' });
      if (!res.ok) throw new Error('Fehler beim Laden der API Keys');
      this._keys = await res.json() as ExternalApiKey[];
    } catch (err) {
      this._error = err instanceof Error ? err.message : 'Unbekannter Fehler';
    } finally {
      this._loadingKeys = false;
    }
  }

  private async _generateKey(): Promise<void> {
    if (!this._newKeyName.trim() || this._generatingKey) return;
    this._generatingKey = true;
    this._error = '';
    this._successMessage = '';
    try {
      const res = await fetch('/api/external-agent/keys', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: this._newKeyName.trim(), rateLimit: this._newKeyRateLimit }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({ error: 'Fehler beim Generieren' })) as { error: string };
        throw new Error(data.error || 'Fehler beim Generieren');
      }
      const result = await res.json() as GenerateKeyResult;
      this._generatedKey = result.plaintext;
      this._newKeyName = '';
      await this._loadKeys();
    } catch (err) {
      this._error = err instanceof Error ? err.message : 'Unbekannter Fehler';
    } finally {
      this._generatingKey = false;
    }
  }

  private async _deleteKey(id: number): Promise<void> {
    if (!window.confirm('API Key wirklich widerrufen? Diese Aktion kann nicht rueckgaengig gemacht werden.')) return;
    this._error = '';
    this._successMessage = '';
    try {
      const res = await fetch(`/api/external-agent/keys/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({ error: 'Fehler beim Widerrufen' })) as { error: string };
        throw new Error(data.error || 'Fehler beim Widerrufen');
      }
      this._successMessage = 'API Key widerrufen';
      await this._loadKeys();
    } catch (err) {
      this._error = err instanceof Error ? err.message : 'Unbekannter Fehler';
    }
  }

  private async _copyKey(): Promise<void> {
    if (!this._generatedKey) return;
    try {
      await navigator.clipboard.writeText(this._generatedKey);
      this._copySuccess = true;
      setTimeout(() => { this._copySuccess = false; }, 2000);
    } catch {
      this._error = 'Kopieren in Zwischenablage fehlgeschlagen';
    }
  }

  private _dismissGeneratedKey(): void {
    this._generatedKey = null;
    this._copySuccess = false;
  }

  private async _loadProjects(): Promise<void> {
    this._loadingProjects = true;
    try {
      const [pseudoRes, projectsRes, sharedRes] = await Promise.all([
        fetch('/api/pseudo-projects', { credentials: 'include' }),
        fetch('/api/projects', { credentials: 'include' }),
        fetch('/api/external-agent/shared-projects', { credentials: 'include' }),
      ]);
      if (!pseudoRes.ok) throw new Error('Fehler beim Laden der Pseudo-Projekte');
      if (!projectsRes.ok) throw new Error('Fehler beim Laden der Projekte');
      if (!sharedRes.ok) throw new Error('Fehler beim Laden der Freigaben');

      this._pseudoProjects = await pseudoRes.json() as PseudoProject[];
      this._projects = (await projectsRes.json() as Project[]).filter(p => p.status === 'active');
      const shared = await sharedRes.json() as SharedProject[];

      this._sharedPseudoIds = new Set(
        shared.filter(s => s.pseudo_project_id !== null).map(s => s.pseudo_project_id!)
      );
      this._sharedProjectIds = new Set(
        shared.filter(s => s.project_id !== null).map(s => s.project_id!)
      );
    } catch (err) {
      this._error = err instanceof Error ? err.message : 'Unbekannter Fehler';
    } finally {
      this._loadingProjects = false;
    }
  }

  private async _togglePseudoProject(id: number): Promise<void> {
    if (this._togglingId !== null) return;
    this._togglingId = `pseudo-${id}`;
    this._error = '';
    try {
      if (this._sharedPseudoIds.has(id)) {
        const res = await fetch(`/api/external-agent/shared-projects/${id}`, {
          method: 'DELETE',
          credentials: 'include',
        });
        if (!res.ok) throw new Error('Fehler beim Entziehen der Freigabe');
        const next = new Set(this._sharedPseudoIds);
        next.delete(id);
        this._sharedPseudoIds = next;
      } else {
        const res = await fetch('/api/external-agent/shared-projects', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ pseudoProjectId: id }),
        });
        if (!res.ok) throw new Error('Fehler beim Freigeben des Projekts');
        const next = new Set(this._sharedPseudoIds);
        next.add(id);
        this._sharedPseudoIds = next;
      }
    } catch (err) {
      this._error = err instanceof Error ? err.message : 'Unbekannter Fehler';
    } finally {
      this._togglingId = null;
    }
  }

  private async _toggleProject(id: number): Promise<void> {
    if (this._togglingId !== null) return;
    this._togglingId = `project-${id}`;
    this._error = '';
    try {
      if (this._sharedProjectIds.has(id)) {
        const res = await fetch(`/api/external-agent/shared-projects/project/${id}`, {
          method: 'DELETE',
          credentials: 'include',
        });
        if (!res.ok) throw new Error('Fehler beim Entziehen der Freigabe');
        const next = new Set(this._sharedProjectIds);
        next.delete(id);
        this._sharedProjectIds = next;
      } else {
        const res = await fetch('/api/external-agent/shared-projects/project', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ projectId: id }),
        });
        if (!res.ok) throw new Error('Fehler beim Freigeben des Projekts');
        const next = new Set(this._sharedProjectIds);
        next.add(id);
        this._sharedProjectIds = next;
      }
    } catch (err) {
      this._error = err instanceof Error ? err.message : 'Unbekannter Fehler';
    } finally {
      this._togglingId = null;
    }
  }

  private _onNewKeyNameInput(e: Event): void {
    this._newKeyName = (e.target as HTMLInputElement).value;
    this._successMessage = '';
  }

  private _onNewKeyRateLimitInput(e: Event): void {
    const val = Number((e.target as HTMLInputElement).value);
    if (!Number.isNaN(val) && val > 0) {
      this._newKeyRateLimit = val;
    }
  }

  private _renderApiKeysSection() {
    return html`
      <div class="cp-ext-agent__section">
        <h4 class="cp-ext-agent__section-title">API Keys</h4>

        ${this._generatedKey ? html`
          <div class="cp-ext-agent__generated-key">
            <p class="cp-ext-agent__generated-key-hint">
              Dieser Key wird nur einmal angezeigt. Kopiere ihn jetzt!
            </p>
            <div class="cp-ext-agent__key-display">
              <code class="cp-ext-agent__key-code">${this._generatedKey}</code>
              <button
                class="cp-ext-agent__btn ${this._copySuccess ? 'cp-ext-agent__btn--success' : 'cp-ext-agent__btn--secondary'}"
                @click=${this._copyKey}
              >${this._copySuccess ? 'Kopiert!' : 'Kopieren'}</button>
            </div>
            <button
              class="cp-ext-agent__btn cp-ext-agent__btn--ghost"
              @click=${this._dismissGeneratedKey}
            >Schliessen</button>
          </div>
        ` : nothing}

        ${this._loadingKeys ? html`
          <p class="cp-ext-agent__loading">Keys werden geladen...</p>
        ` : html`
          ${this._keys.length > 0 ? html`
            <table class="cp-ext-agent__table">
              <thead>
                <tr>
                  <th class="cp-ext-agent__th">Name</th>
                  <th class="cp-ext-agent__th">Key</th>
                  <th class="cp-ext-agent__th">Rate Limit</th>
                  <th class="cp-ext-agent__th"></th>
                </tr>
              </thead>
              <tbody>
                ${this._keys.map(key => html`
                  <tr class="cp-ext-agent__tr">
                    <td class="cp-ext-agent__td">${key.name}</td>
                    <td class="cp-ext-agent__td">
                      <code class="cp-ext-agent__key-prefix">eai_${key.key_prefix}...</code>
                    </td>
                    <td class="cp-ext-agent__td">${key.rate_limit}/min</td>
                    <td class="cp-ext-agent__td cp-ext-agent__td--actions">
                      <button
                        class="cp-ext-agent__btn cp-ext-agent__btn--danger"
                        @click=${() => this._deleteKey(key.id)}
                      >Widerrufen</button>
                    </td>
                  </tr>
                `)}
              </tbody>
            </table>
          ` : html`
            <p class="cp-ext-agent__empty">Keine API Keys vorhanden.</p>
          `}
        `}

        <div class="cp-ext-agent__generate-form">
          <h5 class="cp-ext-agent__subsection-title">Neuen Key generieren</h5>
          <div class="cp-ext-agent__form-row">
            <input
              class="cp-ext-agent__input"
              type="text"
              placeholder="Name, z.B. Thomas OpenClaw"
              .value=${this._newKeyName}
              @input=${this._onNewKeyNameInput}
            />
            <input
              class="cp-ext-agent__input cp-ext-agent__input--number"
              type="number"
              min="1"
              max="10000"
              title="Rate Limit (Anfragen/Minute)"
              .value=${String(this._newKeyRateLimit)}
              @input=${this._onNewKeyRateLimitInput}
            />
            <button
              class="cp-ext-agent__btn cp-ext-agent__btn--primary"
              @click=${this._generateKey}
              ?disabled=${!this._newKeyName.trim() || this._generatingKey}
            >${this._generatingKey ? 'Generiert...' : 'Key generieren'}</button>
          </div>
        </div>
      </div>
    `;
  }

  private _renderProjectsSection() {
    const hasProjects = this._projects.length > 0;
    const hasPseudoProjects = this._pseudoProjects.length > 0;

    return html`
      <div class="cp-ext-agent__section">
        <h4 class="cp-ext-agent__section-title">Projekt-Freigabe</h4>
        ${this._loadingProjects ? html`
          <p class="cp-ext-agent__loading">Projekte werden geladen...</p>
        ` : html`
          ${!hasProjects && !hasPseudoProjects ? html`
            <p class="cp-ext-agent__empty">Keine Projekte vorhanden.</p>
          ` : nothing}

          ${hasProjects ? html`
            <h5 class="cp-ext-agent__subsection-title">Projekte</h5>
            <ul class="cp-ext-agent__project-list">
              ${this._projects.map(project => {
                const isShared = this._sharedProjectIds.has(project.id);
                const isToggling = this._togglingId === `project-${project.id}`;
                return html`
                  <li class="cp-ext-agent__project-item">
                    <span class="cp-ext-agent__project-name">${project.name}</span>
                    <button
                      class="cp-ext-agent__toggle ${isShared ? 'cp-ext-agent__toggle--on' : 'cp-ext-agent__toggle--off'}"
                      @click=${() => this._toggleProject(project.id)}
                      ?disabled=${isToggling}
                      aria-label="${isShared ? 'Freigabe entziehen' : 'Projekt freigeben'}"
                      aria-pressed="${isShared}"
                    >
                      <span class="cp-ext-agent__toggle-knob"></span>
                    </button>
                  </li>
                `;
              })}
            </ul>
          ` : nothing}

          ${hasPseudoProjects ? html`
            <h5 class="cp-ext-agent__subsection-title">Todo-Gruppen</h5>
            <ul class="cp-ext-agent__project-list">
              ${this._pseudoProjects.map(project => {
                const isShared = this._sharedPseudoIds.has(project.id);
                const isToggling = this._togglingId === `pseudo-${project.id}`;
                return html`
                  <li class="cp-ext-agent__project-item">
                    <span class="cp-ext-agent__project-name">${project.name}</span>
                    <button
                      class="cp-ext-agent__toggle ${isShared ? 'cp-ext-agent__toggle--on' : 'cp-ext-agent__toggle--off'}"
                      @click=${() => this._togglePseudoProject(project.id)}
                      ?disabled=${isToggling}
                      aria-label="${isShared ? 'Freigabe entziehen' : 'Projekt freigeben'}"
                      aria-pressed="${isShared}"
                    >
                      <span class="cp-ext-agent__toggle-knob"></span>
                    </button>
                  </li>
                `;
              })}
            </ul>
          ` : nothing}
        `}
      </div>
    `;
  }

  private _renderMcpStatusSection() {
    const activeKeyCount = this._keys.filter(k => k.enabled === 1).length;
    return html`
      <div class="cp-ext-agent__section">
        <h4 class="cp-ext-agent__section-title">MCP Server Status</h4>
        <div class="cp-ext-agent__status-grid">
          <div class="cp-ext-agent__status-item">
            <span class="cp-ext-agent__status-label">Port</span>
            <span class="cp-ext-agent__status-value">3005</span>
          </div>
          <div class="cp-ext-agent__status-item">
            <span class="cp-ext-agent__status-label">Aktive Keys</span>
            <span class="cp-ext-agent__status-value">${activeKeyCount}</span>
          </div>
        </div>
      </div>
    `;
  }

  render() {
    return html`
      <div class="cp-ext-agent">
        <div class="cp-ext-agent__header">
          <h3 class="cp-ext-agent__title">External Agent Interface</h3>
        </div>

        ${this._error ? html`
          <div class="cp-ext-agent__error">${this._error}</div>
        ` : nothing}

        ${this._successMessage ? html`
          <div class="cp-ext-agent__success">${this._successMessage}</div>
        ` : nothing}

        ${this._renderApiKeysSection()}
        ${this._renderProjectsSection()}
        ${this._renderMcpStatusSection()}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-external-agent-settings': CpExternalAgentSettings;
  }
}
